import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";

/**
 * Loitering-Munition (LM) call-for-fire console.
 *
 * Lists LM missions ingested from the CHACaL-LM ATAK plugin (mission_type='LM'),
 * shows an LM-specific layout for the selected one (warhead / burst / geometries /
 * RF jammers / abort), and exports a QGroundControl `.plan` for handoff to a GCS.
 *
 * Self-contained: fetches its own data from the backend so wiring into the FDC
 * page stays to a single tool slot. Auto-selects the FDC's current mission when
 * that mission is itself an LM strike.
 */
export default function LmMissionPanel({ backendUrl, currentMissionId, mission: controlledMission }) {
  // Controlled mode: the parent (FdcPage) drives which mission is shown by
  // passing the loaded detail, so the internal list/selector is hidden. Standalone
  // mode keeps its own selector (used when the panel is opened on its own).
  const controlled = !!controlledMission;

  const [missions, setMissions] = useState([]);
  const [selectedId, setSelectedId] = useState(currentMissionId || "");
  const [fetchedDetail, setFetchedDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);
  const [mavConn, setMavConn] = useState("udpout:127.0.0.1:14550");
  const [mavBusy, setMavBusy] = useState(false);
  const [mavMsg, setMavMsg] = useState(null); // {ok, text}
  const [imageUrl, setImageUrl] = useState(null);

  const detail = controlled ? controlledMission : fetchedDetail;
  const activeId = controlled ? controlledMission?.cff_id : selectedId;

  const fetchList = useCallback(async () => {
    if (controlled) return;
    setError("");
    try {
      const res = await axios.get(`${backendUrl}/lm/missions`, { timeout: 12000 });
      const list = res.data?.missions || [];
      setMissions(list);
      // Auto-select: the FDC's current mission if it is an LM one, else the newest.
      setSelectedId((prev) => {
        if (prev && list.some((m) => m.cff_id === prev)) return prev;
        if (currentMissionId && list.some((m) => m.cff_id === currentMissionId)) {
          return currentMissionId;
        }
        return list[0]?.cff_id || "";
      });
    } catch (e) {
      setError("Could not load LM missions.");
    }
  }, [backendUrl, currentMissionId, controlled]);

  useEffect(() => {
    if (controlled) return undefined;
    fetchList();
    const t = setInterval(fetchList, 15000); // pick up freshly-received strikes
    return () => clearInterval(t);
  }, [fetchList, controlled]);

  useEffect(() => {
    if (controlled || !selectedId) {
      setFetchedDetail(null);
      return undefined;
    }
    let alive = true;
    setLoading(true);
    axios
      .get(`${backendUrl}/lm/${encodeURIComponent(selectedId)}`, { timeout: 12000 })
      .then((res) => {
        if (alive) setFetchedDetail(res.data?.mission || null);
      })
      .catch(() => {
        if (alive) setError("Could not load the selected LM mission.");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [backendUrl, selectedId, controlled]);

  // Fetch the target image (auth blob → object URL) when the mission has one.
  useEffect(() => {
    setImageUrl(null);
    if (!detail?.has_image || !activeId) return undefined;
    let alive = true;
    let objUrl = null;
    axios
      .get(`${backendUrl}/lm/${encodeURIComponent(activeId)}/image`, {
        responseType: "blob", timeout: 15000,
      })
      .then((res) => {
        if (!alive) return;
        objUrl = window.URL.createObjectURL(res.data);
        setImageUrl(objUrl);
      })
      .catch(() => { /* no image / not authed */ });
    return () => {
      alive = false;
      if (objUrl) window.URL.revokeObjectURL(objUrl);
    };
  }, [backendUrl, activeId, detail?.has_image]);

  const exportPlan = async () => {
    if (!activeId) return;
    setExporting(true);
    setError("");
    try {
      // Auth header is injected by the global axios interceptor; a blob keeps the
      // download in-app instead of navigating away.
      const res = await axios.get(
        `${backendUrl}/lm/${encodeURIComponent(activeId)}/qgc.plan`,
        { responseType: "blob", timeout: 15000 },
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${activeId}.plan`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      const msg = e?.response?.status === 422
        ? "This LM mission has no target geometry to export."
        : "QGroundControl export failed.";
      setError(msg);
    } finally {
      setExporting(false);
    }
  };

  const uploadMavlink = async () => {
    if (!activeId) return;
    setMavBusy(true);
    setMavMsg(null);
    try {
      const res = await axios.post(
        `${backendUrl}/lm/${encodeURIComponent(activeId)}/mavlink-upload`,
        { connection: mavConn },
        { timeout: 40000 },
      );
      const d = res.data || {};
      setMavMsg({ ok: d.status === "success", text: d.message || (d.status === "success" ? "Mission uploaded." : "Upload failed.") });
    } catch (e) {
      const d = e?.response?.data;
      setMavMsg({ ok: false, text: d?.message || "MAVLink upload failed." });
    } finally {
      setMavBusy(false);
    }
  };

  const geoms = detail?.geometries || {};
  const latlon = (p) =>
    p ? `${p[0].toFixed(5)}, ${p[1].toFixed(5)}` : "—";
  const altOf = (g) =>
    g && g.altitudeAMSL != null ? `${g.altitudeAMSL} m AMSL` : "—";
  // Prefer the object's own name/callsign (assigned "CFF · <field>" for a drawn
  // shape, or the operator's own name for a selected one) over the raw geometry
  // type. Older missions with no name fall back to the geometry type.
  const altSuffix = (g) => (g && g.altitudeAMSL != null ? ` · ${g.altitudeAMSL} m AMSL` : "");
  const gLabel = (g) => (g ? `${g.name || g.geometryType || "—"}${altSuffix(g)}` : "—");

  const rfLine = useMemo(() => {
    const rf = detail?.rf_obstacles || {};
    const jammed = rf.jammed || [];
    if (!jammed.length && !rf.additional) return null;
    return [...jammed, ...(rf.additional ? [`+ ${rf.additional}`] : [])].join(", ");
  }, [detail]);

  return (
    <Box>
      {!controlled && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
          <FormControl size="small" sx={{ minWidth: 240, flex: 1 }}>
            <InputLabel id="lm-select-label">LM mission</InputLabel>
            <Select
              labelId="lm-select-label"
              label="LM mission"
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
              displayEmpty
            >
              {missions.length === 0 && (
                <MenuItem value="" disabled>
                  No LM missions received
                </MenuItem>
              )}
              {missions.map((m) => (
                <MenuItem key={m.cff_id} value={m.cff_id}>
                  {m.cff_id} — {m.lm_mission_type || "?"} — {m.target_type || "?"}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button size="small" variant="outlined" onClick={fetchList}>
            Refresh
          </Button>
        </Stack>
      )}

      {error && (
        <Typography variant="body2" sx={{ color: "error.main", mb: 1 }}>
          {error}
        </Typography>
      )}

      {!selectedId && !loading && (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          When the CHACaL-LM ATAK plugin sends a call-for-fire, it appears here with
          an LM-specific layout and a QGroundControl export.
        </Typography>
      )}

      {loading && (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          Loading…
        </Typography>
      )}

      {detail && !loading && (
        <Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1.5, gap: 1 }}>
            <Chip size="small" color="warning" label={`LM · ${detail.lm_mission_type || "?"}`} />
            <Chip
              size="small"
              variant="outlined"
              label={`${detail.warhead_type || "?"} / ${detail.burst_type || "?"}`}
            />
            {detail.burst_altitude_amsl != null && (
              <Chip size="small" variant="outlined" label={`Burst @ ${detail.burst_altitude_amsl} m AMSL`} />
            )}
          </Stack>

          <Field label="CFF ID" value={detail.cff_id} mono />
          <Field label="Target type" value={detail.target_type} />
          <Field label="Description" value={detail.target_description} />
          <Field label="Target point" value={latlon(detail.target_point)} mono />
          <Field
            label="Altitude datum"
            value={detail.altitude_datum
              ? `${detail.altitude_datum} (entered as ${detail.altitude_input_mode || "?"})`
              : null}
          />

          {imageUrl && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ color: "text.secondary", mb: 0.5 }}>
                Target image
              </Typography>
              <a href={imageUrl} target="_blank" rel="noreferrer">
                <img
                  src={imageUrl}
                  alt="target"
                  style={{ maxWidth: "100%", borderRadius: 6, display: "block", border: "1px solid rgba(255,255,255,0.15)" }}
                />
              </a>
            </Box>
          )}

          <Divider sx={{ my: 1.5 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
            Geometry
          </Typography>
          <Field label="Target" value={gLabel(geoms.target)} />
          <Field label="Limits" value={gLabel(geoms.limits)} />
          <Field label="Ditch area" value={gLabel(geoms.ditch)} />
          <Field
            label="Mandatory route"
            value={geoms.route
              ? `${geoms.route.name || "Route"} · ${geoms.route.vertices} waypoint(s)${altSuffix(geoms.route)}`
              : "—"}
          />
          <Field
            label="Physical obstacles"
            value={(geoms.obstacles && geoms.obstacles.length)
              ? geoms.obstacles
                  .map((o) => `${o.name || o.type || "?"}${o.altitudeAMSL != null ? ` @${o.altitudeAMSL}m` : ""}`)
                  .join(", ")
              : "—"}
          />
          <Field label="RF obstacles" value={rfLine || "—"} />

          <Divider sx={{ my: 1.5 }} />
          <Field label="Instructions for abort" value={detail.abort_instructions} />

          <Button
            fullWidth
            variant="contained"
            color="warning"
            onClick={exportPlan}
            disabled={exporting || !detail.target_point}
            sx={{ mt: 2, fontWeight: 700 }}
          >
            {exporting ? "Preparing…" : "Export to QGroundControl (.plan)"}
          </Button>
          <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5 }}>
            Downloads a QGC plan (route → waypoints, target → loiter, limits/ditch →
            geofences). Open it in QGroundControl · Plan · Open.
          </Typography>

          <Divider sx={{ my: 1.5 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
            Live MAVLink upload
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              size="small"
              label="MAVLink endpoint"
              value={mavConn}
              onChange={(e) => setMavConn(e.target.value)}
              sx={{ flex: 1 }}
              placeholder="udpout:host:port"
            />
            <Button
              variant="outlined"
              color="warning"
              onClick={uploadMavlink}
              disabled={mavBusy || !detail.target_point || !mavConn.trim()}
              sx={{ whiteSpace: "nowrap", fontWeight: 700 }}
            >
              {mavBusy ? "Uploading…" : "Send to vehicle"}
            </Button>
          </Stack>
          {mavMsg && (
            <Typography
              variant="caption"
              sx={{ display: "block", mt: 0.5, color: mavMsg.ok ? "success.main" : "error.main" }}
            >
              {mavMsg.text}
            </Typography>
          )}
          <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5 }}>
            Uploads the mission over MAVLink to a vehicle / SITL / router (e.g.
            udpout:127.0.0.1:14550). Geofences go via the .plan file for now.
          </Typography>
        </Box>
      )}
    </Box>
  );
}

function Field({ label, value, mono }) {
  return (
    <Box sx={{ display: "flex", gap: 1, py: 0.35 }}>
      <Typography variant="body2" sx={{ color: "text.secondary", minWidth: 132 }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ fontWeight: 600, fontFamily: mono ? "monospace" : undefined, wordBreak: "break-word" }}
      >
        {value || "—"}
      </Typography>
    </Box>
  );
}
